import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';
import { useCategories } from '../../../api/queries/menu.queries';
import { useDeleteCategory } from '../../../api/mutations/menu.mutations';
import CategoryEditModal from './CategoryEditModal';
import type { MenuCategory } from '../../../api/queries/menu.queries';

function CategoryManager() {
  const { t, i18n } = useTranslation();
  const { data: categories, refetch } = useCategories();
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MenuCategory | undefined>(undefined);

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const handleCreate = () => {
    setEditingCategory(undefined);
    setShowModal(true);
  };

  const handleEdit = (category: MenuCategory) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleSuccess = () => {
    refetch();
  };

  const handleDelete = async (categoryId: string) => {
    if (confirm(t('menu.confirmDelete') || 'Are you sure you want to delete this category?')) {
      const deleteMutation = useDeleteCategory(categoryId);
      try {
        await deleteMutation.mutateAsync();
        refetch();
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">{t('menu.categories')}</h2>
        <button
          onClick={handleCreate}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('menu.addCategory') || 'Add Category'}
        </button>
      </div>

      {/* Categories list */}
      <div className="space-y-2">
        {categories?.map((category) => (
          <div
            key={category._id}
            className="rounded-lg border bg-card p-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors"
          >
            <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
            <div className="flex-1">
              <div className="font-medium">{getLangValue(category.name)}</div>
              <div className="text-xs text-muted-foreground">
                Order: {category.displayOrder} • {category.isActive ? t('menu.active') : t('menu.inactive')}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEdit(category)}
                className="rounded p-2 hover:bg-secondary transition-colors"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(category._id)}
                className="rounded p-2 hover:bg-destructive/10 text-destructive transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {showModal && (
        <CategoryEditModal
          category={editingCategory}
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

export default CategoryManager;
